import React from "react";
import { Helmet } from "react-helmet-async";

const LAST_UPDATED = "February 2026";
const CONTACT_EMAIL = "sanguineaccent@gmail.com";
const SITE_URL = "https://sanguineaccent.com";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-playfair text-xl text-[#1c1917] mb-3">{title}</h2>
    <div className="text-stone-600 font-sourcesans text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy — Sanguine Accent</title>
      <meta
        name="description"
        content="Privacy Policy for Sanguine Accent. Learn how we collect, use, and protect your personal information."
      />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/privacy`} />
    </Helmet>

    <div className="min-h-screen bg-[#f5f3ea] pt-28 md:pt-44 pb-20 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-playfair text-3xl md:text-4xl text-[#1c1917] mb-2">
          Privacy Policy
        </h1>
        <p className="text-xs text-stone-400 font-sourcesans mb-10">
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="Who we are">
          <p>
            Sanguine Accent is an independent handmade shop run by Anna Gapyuk,
            selling handmade string art greeting cards. This site is at{" "}
            <a href={SITE_URL} className="text-[#BC3F4A] underline">
              {SITE_URL}
            </a>
            .
          </p>
        </Section>

        <Section title="What data we collect and why">
          <p>
            <strong className="text-[#1c1917]">Account information.</strong>{" "}
            When you register, we collect your name and email address to
            identify your account and process orders. Your password is hashed
            and never stored in plain text.
          </p>
          <p>
            <strong className="text-[#1c1917]">Order information.</strong>{" "}
            When you place an order, we collect your shipping address and phone
            number to fulfill and deliver it. You can view your order history
            and status in your account at any time.
          </p>
          <p>
            <strong className="text-[#1c1917]">Payment information.</strong>{" "}
            Payments are handled entirely by Stripe. We never see, receive, or
            store your card number or any payment credentials. See{" "}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#BC3F4A] underline"
            >
              stripe.com/privacy
            </a>
            .
          </p>
          <p>
            <strong className="text-[#1c1917]">Authentication.</strong>{" "}
            We use a secure HTTP-only cookie to keep you logged in. It cannot
            be accessed by JavaScript and is only sent over HTTPS. We do not
            use tracking, analytics, or advertising cookies.
          </p>
          <p>
            <strong className="text-[#1c1917]">Wishlist and reviews.</strong>{" "}
            If you save items or leave a review, that data is stored linked to
            your account.
          </p>
        </Section>

        <Section title="How we use your data">
          <p>We use your data only to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Process and fulfill your orders</li>
            <li>Maintain your account and order history</li>
            <li>Respond to support requests</li>
          </ul>
          <p>
            We do not sell, rent, or share your personal data with any third
            party for marketing purposes.
          </p>
        </Section>

        <Section title="Data storage and security">
          <p>
            Account and order data is stored in MongoDB Atlas. All connections
            are encrypted via HTTPS/SSL. Your session token is stored in an
            HTTP-only cookie — it is never exposed to JavaScript or stored in
            localStorage.
          </p>
          <p>
            We retain your data for as long as your account is active. You may
            request deletion at any time by emailing us.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            We use one cookie — a session token that keeps you logged in. It is
            strictly necessary for the site to function and is exempt from
            consent requirements under GDPR. We do not use tracking, analytics,
            or advertising cookies.
          </p>
        </Section>

        <Section title="Your rights">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p>
            Email{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#BC3F4A] underline"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            and we will respond within 30 days.
          </p>
        </Section>

        <Section title="Third-party services">
          <p>
            We use the following services, each governed by their own privacy
            policy:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              <strong className="text-[#1c1917]">Stripe</strong> — payment
              processing
            </li>
            <li>
              <strong className="text-[#1c1917]">Cloudinary</strong> — image
              hosting
            </li>
            <li>
              <strong className="text-[#1c1917]">MongoDB Atlas</strong> —
              database hosting
            </li>
          </ul>
        </Section>

        <Section title="Contact">
          <p>
            Questions? Email{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#BC3F4A] underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  </>
);

export default PrivacyPolicy;